import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import { cargarPersonalEdificio } from "../../utils/personalEdificio";
import {
  FaCalendarAlt,
  FaPlus,
  FaUser,
  FaClock,
  FaTrash,
  FaFilter,
  FaSearch,
} from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import AdminModal from "../../components/AdminModal";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
  tableHeader,
  tableCell,
  modalOverlay,
} from "../../utils/designTokens";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const DAYS_OF_WEEK = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Expandir rango de fechas a un array de strings YYYY-MM-DD
const getDateRange = (from, to) => {
  if (!from) return [];
  const dates = [];
  const current = new Date(from + "T12:00:00");
  const end = to ? new Date(to + "T12:00:00") : current;
  while (current <= end) {
    dates.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const TYPE_LABELS = {
  reglamentaria: "Reglamentaria",
  extraordinaria: "Extraordinaria",
  estudio: "Por Estudio",
  maternidad_paternidad: "Maternidad/Paternidad",
  sin_goce: "Sin Goce de Sueldo",
  enfermedad: "Por Enfermedad",
  licencia: "Licencia",
  otro: "Otro",
};

const TurnosAdmin = () => {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const { selectedBuilding } = useBuilding();
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth());
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [formData, setFormData] = useState({
    employeeid: "",
    date: "",
    dateFrom: "",
    dateTo: "",
    startTime: "08:00",
    endTime: "16:00",
    type: "Regular",
  });
  const [saving, setSaving] = useState(false);
  const [selectedOoO, setSelectedOoO] = useState(null);

  const buildingId = selectedBuilding?.buildingid;

  useEffect(() => {
    if (!buildingId) return;
    fetchEmployees();
  }, [buildingId]);

  useEffect(() => {
    if (!buildingId) return;
    fetchShifts();
    fetchLeaves();
  }, [buildingId, filterMonth, filterYear]);

  // Personal del edificio: employeebuilding por `building` + nombre de la
  // ficha users (utils/personalEdificio). Sin escanear la colección employee.
  const fetchEmployees = async () => {
    try {
      setEmployees(await cargarPersonalEdificio(buildingId));
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
    }
  };

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "employee_shifts"),
        where("buildingid", "==", buildingId)
      );
      const snap = await getDocs(q);
      let allShifts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Ordenar en el cliente para evitar requerir índice compuesto
      allShifts.sort((a, b) => {
        const da = a.date?.toDate ? a.date.toDate() : new Date(a.date);
        const db2 = b.date?.toDate ? b.date.toDate() : new Date(b.date);
        return da - db2;
      });

      // Filtrar por mes/año
      allShifts = allShifts.filter((s) => {
        const d = s.date?.toDate ? s.date.toDate() : new Date(s.date);
        return d.getMonth() === filterMonth && d.getFullYear() === filterYear;
      });

      if (filterEmployee) {
        allShifts = allShifts.filter(
          (s) => String(s.employeeid) === String(filterEmployee)
        );
      }

      setShifts(allShifts);
    } catch (err) {
      console.error("Error fetching shifts:", err);
    }
    setLoading(false);
  };

  const fetchLeaves = async () => {
    try {
      const bId = buildingId;
      // Intentar con buildingid numérico y string
      const q1 = query(
        collection(db, "employee_requests"),
        where("buildingid", "==", Number(bId)),
        where("status", "==", "aprobada")
      );
      const q2 = query(
        collection(db, "employee_requests"),
        where("buildingid", "==", String(bId)),
        where("status", "==", "aprobada")
      );
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const seen = new Set();
      const allLeaves = [];
      [...snap1.docs, ...snap2.docs].forEach((d) => {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          allLeaves.push({ id: d.id, ...d.data() });
        }
      });
      setLeaves(allLeaves);
    } catch (err) {
      console.error("Error fetching leaves:", err);
    }
  };

  useEffect(() => {
    if (!buildingId) return;
    fetchShifts();
  }, [filterEmployee]);

  const openCreateModal = () => {
    setBulkMode(false);
    setFormData({
      employeeid: "",
      date: "",
      dateFrom: "",
      dateTo: "",
      startTime: "08:00",
      endTime: "16:00",
      type: "Regular",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.employeeid) {
      showToast("Seleccioná un empleado", "warning");
      return;
    }
    if (!bulkMode && !formData.date) {
      showToast("Seleccioná una fecha", "warning");
      return;
    }
    if (bulkMode && (!formData.dateFrom || !formData.dateTo)) {
      showToast("Seleccioná el rango de fechas", "warning");
      return;
    }

    setSaving(true);
    try {
      const emp = employees.find(
        (e) => String(e.employeeid) === String(formData.employeeid)
      );
      const empName = emp
        ? `${emp.nombre || ""} ${emp.apellido || ""}`.trim()
        : "";

      if (bulkMode) {
        // Crear turnos para cada día del rango
        const start = new Date(formData.dateFrom + "T00:00:00");
        const end = new Date(formData.dateTo + "T00:00:00");
        const promises = [];

        for (
          let d = new Date(start);
          d <= end;
          d.setDate(d.getDate() + 1)
        ) {
          promises.push(
            addDoc(collection(db, "employee_shifts"), {
              employeeid: formData.employeeid,
              employeeName: empName,
              buildingid: buildingId,
              date: Timestamp.fromDate(new Date(d)),
              startTime: formData.startTime,
              endTime: formData.endTime,
              type: formData.type,
              createdBy: auth.currentUser?.email || "admin",
              createdAt: Timestamp.now(),
            })
          );
        }
        await Promise.all(promises);
      } else {
        await addDoc(collection(db, "employee_shifts"), {
          employeeid: formData.employeeid,
          employeeName: empName,
          buildingid: buildingId,
          date: Timestamp.fromDate(new Date(formData.date + "T00:00:00")),
          startTime: formData.startTime,
          endTime: formData.endTime,
          type: formData.type,
          createdBy: auth.currentUser?.email || "admin",
          createdAt: Timestamp.now(),
        });
      }

      setShowModal(false);
      fetchShifts();
    } catch (err) {
      console.error("Error saving shift:", err);
      showToast("Error al guardar el turno", "error");
    }
    setSaving(false);
  };

  const handleDelete = async (shiftId) => {
    const ok = await confirmDialog("¿Eliminar este turno?", {
      title: "Eliminar turno",
      danger: true,
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "employee_shifts", shiftId));
      setShifts((prev) => prev.filter((s) => s.id !== shiftId));
    } catch (err) {
      console.error("Error deleting shift:", err);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(
      (e) => String(e.employeeid) === String(empId)
    );
    return emp
      ? `${emp.nombre || ""} ${emp.apellido || ""}`.trim()
      : "Empleado";
  };

  // Mapear licencias por fecha para la vista calendario
  const leavesByDate = useMemo(() => {
    const map = {};
    leaves.forEach((leave) => {
      const dates = getDateRange(leave.dateFrom, leave.dateTo);
      dates.forEach((dateStr) => {
        if (!map[dateStr]) map[dateStr] = [];
        map[dateStr].push(leave);
      });
    });
    return map;
  }, [leaves]);

  // Construir días del calendario para el mes seleccionado
  const calendarDays = useMemo(() => {
    const firstDay = new Date(filterYear, filterMonth, 1);
    const lastDay = new Date(filterYear, filterMonth + 1, 0);
    const startDow = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const days = [];
    // Espacios vacíos antes del primer día
    for (let i = 0; i < startDow; i++) {
      days.push(null);
    }
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${filterYear}-${String(filterMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push(dateStr);
    }
    return days;
  }, [filterMonth, filterYear]);

  // Mapear turnos por fecha para el calendario
  const shiftsByDate = useMemo(() => {
    const map = {};
    shifts.forEach((shift) => {
      const d = shift.date?.toDate ? shift.date.toDate() : new Date(shift.date);
      const dateStr = d.toISOString().split("T")[0];
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(shift);
    });
    return map;
  }, [shifts]);

  const formatDate = (val) => {
    if (!val) return "-";
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDayName = (val) => {
    if (!val) return "";
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", { weekday: "long" });
  };

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <EmptyState
          icon={FaCalendarAlt}
          title="Seleccioná un edificio para gestionar turnos"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaCalendarAlt style={{ marginRight: 10, color: colors.primary }} />
          Turnos de Empleados
        </h1>
        <button style={styles.newBtn} onClick={openCreateModal}>
          <FaPlus style={{ marginRight: 8 }} /> Nuevo Turno
        </button>
      </div>

      {/* Filtros */}
      <div style={styles.filtersCard}>
        <div style={styles.filtersHeader}>
          <FaFilter style={{ marginRight: 8, color: colors.primary }} />
          <span style={{ fontWeight: 400 }}>Filtros</span>
        </div>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Empleado</label>
            <select
              style={styles.select}
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
            >
              <option value="">Todos</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.employeeid}>
                  {emp.nombre} {emp.apellido}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Mes</label>
            <select
              style={styles.select}
              value={filterMonth}
              onChange={(e) => setFilterMonth(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={i} value={i}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Año</label>
            <select
              style={styles.select}
              value={filterYear}
              onChange={(e) => setFilterYear(Number(e.target.value))}
            >
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <FaCalendarAlt style={{ fontSize: 20, color: colors.primary }} />
          <div>
            <p style={styles.statValue}>{shifts.length}</p>
            <p style={styles.statLabel}>Turnos del mes</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <FaUser style={{ fontSize: 20, color: colors.primary }} />
          <div>
            <p style={styles.statValue}>
              {new Set(shifts.map((s) => String(s.employeeid))).size}
            </p>
            <p style={styles.statLabel}>Empleados con turnos</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <FaClock style={{ fontSize: 20, color: colors.primary }} />
          <div>
            <p style={styles.statValue}>
              {shifts.filter((s) => s.type === "Extra").length}
            </p>
            <p style={styles.statLabel}>Turnos extra</p>
          </div>
        </div>
      </div>

      {/* Vista calendario */}
      <div style={{ ...styles.tableCard, marginBottom: spacing[6] }}>
        <h3 style={{ margin: "0 0 16px 0", fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black, display: "flex", alignItems: "center", gap: spacing[2] }}>
          <FaCalendarAlt style={{ color: colors.primary }} />
          Calendario - {MONTHS[filterMonth]} {filterYear}
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: spacing[4] }}>
          {DAYS_OF_WEEK.map((d) => (
            <div key={d} style={{ textAlign: "center", fontWeight: 400, fontSize: typography.sizes.sm, color: colors.gray600, padding: "8px 0", textTransform: "uppercase" }}>
              {d}
            </div>
          ))}
          {calendarDays.map((dateStr, idx) => {
            if (!dateStr) {
              return <div key={`empty-${idx}`} style={{ minHeight: 80 }} />;
            }
            const dayNum = parseInt(dateStr.split("-")[2], 10);
            const dayLeaves = leavesByDate[dateStr] || [];
            const dayShifts = shiftsByDate[dateStr] || [];
            const isToday = dateStr === new Date().toISOString().split("T")[0];

            return (
              <div
                key={dateStr}
                style={{
                  minHeight: 80,
                  background: isToday ? colors.primarySubtle : colors.gray50,
                  border: isToday ? `2px solid ${colors.primary}` : `1px solid ${colors.gray200}`,
                  borderRadius: radius.sm,
                  padding: spacing[1],
                  overflow: "hidden",
                }}
              >
                <div style={{ fontSize: typography.sizes.xs, fontWeight: 400, color: isToday ? colors.primary : colors.gray700, marginBottom: 2, textAlign: "right", paddingRight: 4 }}>
                  {dayNum}
                </div>
                {/* Chips de turnos */}
                {dayShifts.slice(0, 2).map((s, i) => (
                  <div key={`s-${i}`} style={{ fontSize: 10, background: colors.successLight, color: colors.successDark, borderRadius: 4, padding: "2px 4px", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {s.employeeName || getEmployeeName(s.employeeid)} {s.startTime}-{s.endTime}
                  </div>
                ))}
                {dayShifts.length > 2 && (
                  <div style={{ fontSize: 9, color: colors.gray600 }}>+{dayShifts.length - 2} más</div>
                )}
                {/* Chips de licencias (OoO) */}
                {dayLeaves.map((l, i) => (
                  <div
                    key={`l-${i}`}
                    onClick={() => setSelectedOoO(l)}
                    style={{
                      fontSize: 10,
                      background: colors.infoLight,
                      color: colors.infoDark,
                      borderRadius: 4,
                      padding: "2px 4px",
                      marginBottom: 2,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: 400,
                    }}
                    title={`OoO - ${l.employeeName || ""} - ${TYPE_LABELS[l.type] || l.type}`}
                  >
                    OoO {l.employeeName || ""}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        {/* Leyenda */}
        <div style={{ display: "flex", gap: spacing[5], fontSize: typography.sizes.sm, color: colors.gray700 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: colors.successLight, display: "inline-block" }} />
            Turno
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: colors.infoLight, display: "inline-block" }} />
            Licencia (OoO)
          </span>
        </div>
      </div>

      {/* Modal detalle de licencia */}
      <AdminModal
        open={!!selectedOoO}
        onClose={() => setSelectedOoO(null)}
        title="Detalle de Licencia"
        size="narrow"
      >
        {selectedOoO && (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
            <div>
              <span style={{ fontWeight: 400, color: colors.gray700, fontSize: typography.sizes.base }}>Empleado:</span>
              <p style={{ margin: "4px 0 0", fontSize: 15, color: colors.black }}>{selectedOoO.employeeName || "—"}</p>
            </div>
            <div>
              <span style={{ fontWeight: 400, color: colors.gray700, fontSize: typography.sizes.base }}>Tipo:</span>
              <p style={{ margin: "4px 0 0", fontSize: 15, color: colors.black }}>
                <span style={{ background: colors.infoLight, color: colors.infoDark, padding: "3px 10px", borderRadius: radius.md, fontSize: typography.sizes.base, fontWeight: 400 }}>
                  OoO — {TYPE_LABELS[selectedOoO.type] || selectedOoO.type}
                </span>
              </p>
            </div>
            <div>
              <span style={{ fontWeight: 400, color: colors.gray700, fontSize: typography.sizes.base }}>Período:</span>
              <p style={{ margin: "4px 0 0", fontSize: 15, color: colors.black }}>
                {selectedOoO.dateFrom || "—"} -> {selectedOoO.dateTo || selectedOoO.dateFrom || "—"}
              </p>
            </div>
            {selectedOoO.title && (
              <div>
                <span style={{ fontWeight: 400, color: colors.gray700, fontSize: typography.sizes.base }}>Motivo:</span>
                <p style={{ margin: "4px 0 0", fontSize: 15, color: colors.black }}>{selectedOoO.title}</p>
              </div>
            )}
            {selectedOoO.description && (
              <div>
                <span style={{ fontWeight: 400, color: colors.gray700, fontSize: typography.sizes.base }}>Descripción:</span>
                <p style={{ margin: "4px 0 0", fontSize: typography.sizes.md, color: colors.gray700 }}>{selectedOoO.description}</p>
              </div>
            )}
          </div>
        )}
      </AdminModal>

      {/* Tabla de turnos */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={{ color: colors.gray600, marginTop: spacing[3] }}>Cargando turnos...</p>
        </div>
      ) : (
        <DataTable
          columns={[
            { key: "date", label: "Fecha", render: (val) => formatDate(val) },
            {
              key: "dayName",
              label: "Día",
              render: (_, row) => (
                <span style={{ textTransform: "capitalize" }}>
                  {getDayName(row.date)}
                </span>
              ),
            },
            {
              key: "employeeName",
              label: "Empleado",
              render: (val, row) => val || getEmployeeName(row.employeeid),
            },
            { key: "startTime", label: "Inicio", render: (val) => val || "-" },
            { key: "endTime", label: "Fin", render: (val) => val || "-" },
            {
              key: "type",
              label: "Tipo",
              render: (val) => (
                <StatusBadge status={val?.toLowerCase()} label={val} />
              ),
            },
            {
              key: "actions",
              label: "",
              render: (_, row) => (
                <button
                  style={styles.deleteBtn}
                  onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
                  title="Eliminar"
                  aria-label="Eliminar"
                >
                  <FaTrash />
                </button>
              ),
            },
          ]}
          data={shifts}
          emptyIcon={FaSearch}
          emptyTitle="No hay turnos para este período"
          emptyDescription="Creá un nuevo turno con el botón de arriba"
        />
      )}

      {/* Modal de creación */}
      <AdminModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Nuevo Turno"
        size="medium"
        footer={
          <>
            <button
              style={styles.cancelBtn}
              onClick={() => setShowModal(false)}
            >
              Cancelar
            </button>
            <button
              style={styles.saveBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : bulkMode
                ? "Crear Turnos"
                : "Crear Turno"}
            </button>
          </>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Empleado *</label>
          <select
            style={styles.select}
            value={formData.employeeid}
            onChange={(e) =>
              setFormData({ ...formData, employeeid: e.target.value })
            }
          >
            <option value="">Seleccionar empleado</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.employeeid}>
                {emp.nombre} {emp.apellido}
              </option>
            ))}
          </select>
        </div>

        {/* Toggle modo masivo */}
        <div style={styles.toggleRow}>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={bulkMode}
              onChange={(e) => setBulkMode(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Asignar para rango de fechas
          </label>
        </div>

        {bulkMode ? (
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Desde *</label>
              <input
                type="date"
                style={styles.input}
                value={formData.dateFrom}
                onChange={(e) =>
                  setFormData({ ...formData, dateFrom: e.target.value })
                }
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Hasta *</label>
              <input
                type="date"
                style={styles.input}
                value={formData.dateTo}
                onChange={(e) =>
                  setFormData({ ...formData, dateTo: e.target.value })
                }
              />
            </div>
          </div>
        ) : (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Fecha *</label>
            <input
              type="date"
              style={styles.input}
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
            />
          </div>
        )}

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Hora inicio</label>
            <input
              type="time"
              style={styles.input}
              value={formData.startTime}
              onChange={(e) =>
                setFormData({ ...formData, startTime: e.target.value })
              }
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Hora fin</label>
            <input
              type="time"
              style={styles.input}
              value={formData.endTime}
              onChange={(e) =>
                setFormData({ ...formData, endTime: e.target.value })
              }
            />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Tipo de turno</label>
          <select
            style={styles.select}
            value={formData.type}
            onChange={(e) =>
              setFormData({ ...formData, type: e.target.value })
            }
          >
            <option value="Regular">Regular</option>
            <option value="Extra">Extra</option>
            <option value="Feriado">Feriado</option>
          </select>
        </div>
      </AdminModal>
    </div>
  );
};

// ─── Estilos migrados a designTokens ────────────────────────────────────────
const styles = {
  // Contenedor principal de la página
  container: {
    ...pageContainer,
  },
  // Cabecera con título y botón nuevo
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing[5],
    flexWrap: "wrap",
    gap: spacing[3],
  },
  // Título de la página
  title: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
    display: "flex",
    alignItems: "center",
  },
  // Botón "Nuevo Turno"
  newBtn: {
    ...btnPrimary,
  },
  // Tarjeta de filtros
  filtersCard: {
    ...cardBase,
    marginBottom: spacing[6],
  },
  // Encabezado de la sección de filtros
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: spacing[4],
    fontSize: 15,
    color: colors.black,
  },
  // Fila de filtros
  filtersRow: {
    display: "flex",
    gap: spacing[4],
    flexWrap: "wrap",
  },
  // Grupo individual de filtro
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 160,
  },
  // Etiqueta de filtro
  filterLabel: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
  },
  // Select reutilizable
  select: {
    ...inputBase,
    padding: "8px 12px",
  },
  // Fila de tarjetas de estadísticas
  statsRow: {
    display: "flex",
    gap: spacing[4],
    marginBottom: spacing[6],
    flexWrap: "wrap",
  },
  // Tarjeta de estadística individual
  statCard: {
    ...cardBase,
    flex: 1,
    minWidth: 160,
    padding: spacing[5],
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  // Valor numérico de la estadística
  statValue: {
    fontSize: 20,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  // Etiqueta de la estadística
  statLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    margin: 0,
  },
  // Tarjeta contenedora de tabla/calendario
  tableCard: {
    ...cardBase,
  },
  // Botón de eliminar turno
  deleteBtn: {
    ...btnGhost,
    color: colors.error,
    fontSize: typography.sizes.md,
    padding: 6,
  },
  // Grupo de formulario
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: spacing[4],
    flex: 1,
  },
  // Etiqueta de campo de formulario
  formLabel: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
  },
  // Input de formulario
  input: {
    ...inputBase,
    padding: "10px 14px",
    boxSizing: "border-box",
  },
  // Fila de formulario (dos campos lado a lado)
  formRow: {
    display: "flex",
    gap: spacing[4],
  },
  // Fila del toggle de modo masivo
  toggleRow: {
    marginBottom: spacing[4],
  },
  // Etiqueta del toggle
  toggleLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: typography.sizes.md,
    color: colors.gray700,
    cursor: "pointer",
  },
  // Botón cancelar del modal
  cancelBtn: {
    ...btnSecondary,
  },
  // Botón guardar del modal
  saveBtn: {
    ...btnPrimary,
  },
  // Contenedor de carga (spinner)
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
  },
  // Spinner de carga
  spinner: {
    width: 36,
    height: 36,
    border: `3px solid ${colors.gray200}`,
    borderTopColor: colors.primary,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

export default TurnosAdmin;
